import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { ToastProvider } from "@a2z/ui";
import {
  I18nProvider,
  getLocaleDirection,
  resolveLocale,
  useTranslate,
} from "@/lib/i18n";

/**
 * Applies the route's locale to the document.
 *
 * `dir` and `lang` are set on `<html>` rather than on a wrapper, because RTL
 * has to reach things that escape the React tree — scrollbars, form controls,
 * and the print stylesheet's `inset-inline-start`.
 */
export function LocaleLayout() {
  const { locale: raw } = useParams();
  const locale = resolveLocale(raw);
  const direction = getLocaleDirection(locale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
  }, [locale, direction]);

  return (
    <I18nProvider locale={locale}>
      <ToastLayer>
        <Outlet />
      </ToastLayer>
    </I18nProvider>
  );
}

/**
 * The feedback layer, mounted once for the whole app.
 *
 * It lives here rather than in `main.tsx` for two reasons: it needs a
 * translated close label, so it has to sit under `I18nProvider`; and it must
 * sit ABOVE the routes so a toast raised by a command survives the navigation
 * that command triggers.
 *
 * Why the app needs it at all: an error rendered by the component that raised
 * it appears wherever that component happens to sit — which on the invoice
 * screen is inside a scroll region, below the fold, behind the fixed action
 * bar the waiter just tapped. A failure the user never sees is a failure they
 * retry blindly. The toast layer is fixed to the viewport, so feedback lands
 * where the user is actually looking.
 */
function ToastLayer({ children }: { children: React.ReactNode }) {
  const t = useTranslate();
  return (
    <ToastProvider closeLabel={t((m) => m.common.close)}>
      {children}
    </ToastProvider>
  );
}
